'use client';

import { captureException } from '@sentry/react';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import {
  createContext,
  type FC,
  type PropsWithChildren,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useAuth } from '@furality/frontend-stack';
import useFlag from '@/lib/hooks/useFlag';
import CentrifugeWorker from '@/lib/workers/centrifuge.worker?worker&url';
import {
  CENTRIFUGE_EVENT_TYPE,
  EVENT_TYPE,
  isWorkerMessage,
  type WorkerMessage,
} from '@/lib/workers/workerTypes';

type CentrifugeEventType = 'ready' | `data:${string}` | `ready:${string}` | `subscribed:${string}`;

export class CentrifugeEvent extends Event {
  public data: unknown;

  constructor(channel: CentrifugeEventType, data: unknown) {
    super(channel);

    this.data = data;
  }
}

// oxlint-disable-next-line max-classes-per-file
class CentrifugeEmitter extends EventTarget {
  public override dispatchEvent(event: CentrifugeEvent): boolean {
    return super.dispatchEvent(event);
  }
}

type CentrifugeInteraction = {
  reloadConnection: () => void;
  postMessage: (message: WorkerMessage) => void;
  addEventListener: CentrifugeEmitter['addEventListener'];
  subscribeChannel: (channel: string) => void;
  unsubscribeChannel: (channel: string) => void;
  removeEventListener: CentrifugeEmitter['removeEventListener'];
  ready: RefObject<boolean>;
  authFailed: boolean;
};

export const CentrifugeContext = createContext<CentrifugeInteraction>({
  reloadConnection: () => undefined,
  postMessage: () => undefined,
  addEventListener: () => undefined,
  removeEventListener: () => undefined,
  subscribeChannel: () => undefined,
  unsubscribeChannel: () => undefined,
  ready: { current: false },
  authFailed: false,
});

const CentrifugeProvider: FC<PropsWithChildren> = ({ children }) => {
  const { auth, renew } = useAuth();
  const featuresReady = useFlagsStatus().flagsReady;
  const realtimeEnabled = useFlag('realtime');

  const workerRef = useRef<Worker | SharedWorker | null>(null);
  const pendingMessagesRef = useRef<WorkerMessage[]>([]);
  const eventsRef = useRef(new CentrifugeEmitter());
  const readyRef = useRef(false);
  const subCountsRef = useRef<Map<string, number>>(new Map());
  const authRetryCountRef = useRef(0);
  const pendingTokenUpdateRef = useRef(false);
  const authTokenRef = useRef(auth?.token);

  const [authFailed, setAuthFailed] = useState(false);

  useEffect(() => {
    authTokenRef.current = auth?.token;
  }, [auth?.token]);

  // Worker handling, creates the worker or shared worker
  useEffect(() => {
    if (workerRef.current || !auth?.token || !featuresReady || !realtimeEnabled) {
      return;
    }

    // Common message handler for both worker and shared worker
    const constructMessageHandler = (postMessage: (message: WorkerMessage) => void) => {
      return (event: MessageEvent<WorkerMessage>) => {
        const data = event.data;

        if (!isWorkerMessage(data)) {
          console.error('INVALID WORKER MESSAGE', data);

          return;
        }

        const [type] = data;

        switch (type) {
          // worker needs auth
          case EVENT_TYPE.CONNECT: {
            postMessage([EVENT_TYPE.CONNECT, authTokenRef.current ?? null]);
            break;
          }

          // connection error
          case EVENT_TYPE.CONNECT_ERROR: {
            const [, message] = data;

            if (typeof message === 'string') {
              captureException(new Error(message));
            } else {
              captureException(new Error('Worker returned unknown error'), { extra: { message } });
            }

            break;
          }

          // custom events
          case EVENT_TYPE.EVENT: {
            const [, eventType, channel, eventData] = data;

            switch (eventType) {
              case CENTRIFUGE_EVENT_TYPE.MESSAGE:
                eventsRef.current.dispatchEvent(new CentrifugeEvent(`data:${channel}`, eventData));
                break;

              case CENTRIFUGE_EVENT_TYPE.SUB_CREATED:
                eventsRef.current.dispatchEvent(new CentrifugeEvent(`subscribed:${channel}`, null));
                break;

              case CENTRIFUGE_EVENT_TYPE.SUB_READY:
                eventsRef.current.dispatchEvent(new CentrifugeEvent(`ready:${channel}`, null));
                break;

              default:
                console.warn('Received unknown event type from worker', {
                  eventType,
                  channel,
                  eventData,
                });
                break;
            }

            break;
          }

          case EVENT_TYPE.READY: {
            readyRef.current = true;
            authRetryCountRef.current = 0;

            setAuthFailed(false);
            eventsRef.current.dispatchEvent(new CentrifugeEvent('ready', null));

            break;
          }

          case EVENT_TYPE.AUTH_ERROR: {
            if (authRetryCountRef.current > 5) {
              captureException(new Error('AUTH ERROR COUNT EXCEEDED 5'));
              setAuthFailed(true);
              postMessage([EVENT_TYPE.DISCONNECT]);
            } else {
              setTimeout(
                () => {
                  authRetryCountRef.current += 1;
                  pendingTokenUpdateRef.current = true;
                  renew();
                },
                1000 + Math.random() * 3000,
              );
            }

            break;
          }

          case EVENT_TYPE.RELOAD: {
            setTimeout(
              () => {
                const timestamp = Date.now();
                window.location.href = window.location.href.includes('?')
                  ? `${window.location.href}&v=${timestamp}`
                  : `${window.location.href}?v=${timestamp}`;
              },
              100 + Math.random() * 1000,
            );
            break;
          }

          default:
            break;
        }
      };
    };

    if (typeof SharedWorker !== 'undefined') {
      const worker = new SharedWorker(CentrifugeWorker, { type: 'module' });
      workerRef.current = worker;

      const postWorkerMessage = (message: WorkerMessage) => {
        worker.port.postMessage(message);
      };

      worker.port.onmessage = constructMessageHandler(postWorkerMessage);

      for (const pendingMessage of pendingMessagesRef.current) {
        postWorkerMessage(pendingMessage);
      }

      pendingMessagesRef.current = [];

      const handleUnload = () => {
        postWorkerMessage([EVENT_TYPE.DISCONNECT]);
        worker.port.close();

        workerRef.current = null;
        readyRef.current = false;
      };

      window.addEventListener('beforeunload', handleUnload);

      // Close worker connection on unmount
      return () => {
        subCountsRef.current.clear();
        window.removeEventListener('beforeunload', handleUnload);
        postWorkerMessage([EVENT_TYPE.DISCONNECT]);
        worker.port.close();

        workerRef.current = null;
        readyRef.current = false;
      };
    }

    // Regular worker
    const worker = new Worker(CentrifugeWorker, { type: 'module' });
    workerRef.current = worker;

    const postWorkerMessage = (message: WorkerMessage) => {
      worker.postMessage(message);
    };

    worker.addEventListener('message', constructMessageHandler(postWorkerMessage));

    for (const pendingMessage of pendingMessagesRef.current) {
      postWorkerMessage(pendingMessage);
    }

    pendingMessagesRef.current = [];

    const handleUnload = () => {
      postWorkerMessage([EVENT_TYPE.DISCONNECT]);

      workerRef.current = null;
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      subCountsRef.current.clear();
      postWorkerMessage([EVENT_TYPE.DISCONNECT]);
      window.removeEventListener('beforeunload', handleUnload);

      workerRef.current = null;
      readyRef.current = false;
    };
  }, [auth?.token, featuresReady, realtimeEnabled, renew]);

  useEffect(() => {
    if (!auth?.token || !pendingTokenUpdateRef.current || !workerRef.current) {
      return;
    }

    pendingTokenUpdateRef.current = false;
    const message = [EVENT_TYPE.CONNECT, auth.token];

    if (typeof SharedWorker !== 'undefined' && workerRef.current instanceof SharedWorker) {
      workerRef.current.port.postMessage(message);
    } else if (workerRef.current instanceof Worker) {
      workerRef.current.postMessage(message);
    }
  }, [auth?.token]);

  const postMessage = (message: WorkerMessage) => {
    if (!realtimeEnabled || !readyRef.current) {
      return;
    }

    if (typeof SharedWorker !== 'undefined' && workerRef.current instanceof SharedWorker) {
      workerRef.current.port.postMessage(message);
    } else if (workerRef.current instanceof Worker) {
      workerRef.current.postMessage(message);
    } else {
      pendingMessagesRef.current.push(message);
    }
  };

  const reloadConnection = () => {
    if (!auth?.token) {
      return;
    }

    subCountsRef.current.clear();

    try {
      postMessage([EVENT_TYPE.RELOAD]);
    } catch (e) {
      captureException(e);
    }
  };

  const subscribeChannel = (channel: string) => {
    const count = subCountsRef.current.get(channel) ?? 0;
    subCountsRef.current.set(channel, count + 1);

    if (count === 0) {
      postMessage([EVENT_TYPE.EVENT, CENTRIFUGE_EVENT_TYPE.SUB, channel]);
    }
  };

  const unsubscribeChannel = (channel: string) => {
    const count = subCountsRef.current.get(channel) ?? 0;

    if (count > 0) {
      subCountsRef.current.set(channel, count - 1);

      if (count === 1) {
        postMessage([EVENT_TYPE.EVENT, CENTRIFUGE_EVENT_TYPE.UNSUB, channel]);
      }
    }
  };

  const addEventListener: typeof CentrifugeEmitter.prototype.addEventListener = (...args) => {
    eventsRef.current.addEventListener(...args);
  };

  const removeEventListener: typeof CentrifugeEmitter.prototype.removeEventListener = (...args) => {
    eventsRef.current.removeEventListener(...args);
  };

  const providerValue = {
    reloadConnection,
    postMessage,
    addEventListener,
    removeEventListener,
    subscribeChannel,
    unsubscribeChannel,
    ready: readyRef,
    authFailed,
  };

  return <CentrifugeContext.Provider value={providerValue}>{children}</CentrifugeContext.Provider>;
};

export default CentrifugeProvider;
